import { useId } from "react";

import { Carousel } from "../../../../core/Carousel.js";
import { CAROUSEL_SLIDES } from "../../../../core/Carousel.styles.js";
import { ChipRadioGroup } from "../../../../core/Chip.js";
import { Section } from "../../../../patterns/Section.js";
import { SectionHeader } from "../../../../patterns/SectionHeader.js";
import { BoxCards, BoxCardsSkeleton, ContentBoxCard } from "../../../shared/BoxCards.js";
import { LoadError } from "../../../shared/LoadError.js";
import type { FeaturedBoxesContent } from "../../homeContent.js";

/**
 * The boxes a tenant featured, with their loading, failed and hidden states. The `grid` and `large`
 * variants lay them in a grid; `rail` sets them on a carousel that scrolls sideways with Previous and Next.
 */
export function HomeFeaturedBoxes({
  variant = "grid",
  ...featured
}: FeaturedBoxesContent & { variant?: "grid" | "large" | "rail" }) {
  const id = useId();
  if (featured.hidden) return null;
  const header = (
    <SectionHeader
      title={featured.title}
      titleId={id}
      subtitle={featured.subtitle}
      action={featured.viewAll}
      size={variant === "rail" ? "sm" : "lg"}
    >
      {featured.filters && (
        <ChipRadioGroup
          label={featured.filters.label}
          options={featured.filters.options}
          value={featured.filters.value}
          onValueChange={featured.filters.onChange}
        />
      )}
    </SectionHeader>
  );
  const rail = variant === "rail" && featured.errorMessage === null && featured.cards !== null;
  return (
    // On a rail the Carousel is the region, named by the title, so the section names nothing.
    <Section
      aria-labelledby={rail ? undefined : id}
      aria-busy={featured.cards === null || undefined}
    >
      {rail ? (
        <Carousel label={featured.title} heading={header} slideClassName={CAROUSEL_SLIDES.tiles}>
          {(featured.cards ?? []).map((card) => (
            <ContentBoxCard key={card.box.id} card={card} currency={featured.currency} />
          ))}
        </Carousel>
      ) : (
        <div className="flex flex-col gap-6">
          {header}
          {featured.errorMessage !== null ? (
            <LoadError message={featured.errorMessage} />
          ) : featured.cards === null ? (
            <BoxCardsSkeleton
              columns={variant === "large" ? "large" : "row"}
              count={variant === "large" ? 3 : 5}
            />
          ) : (
            <BoxCards
              cards={featured.cards}
              currency={featured.currency}
              columns={variant === "large" ? "large" : "row"}
            />
          )}
        </div>
      )}
    </Section>
  );
}
